import {
  DashboardLayout,
  DashboardWidgetId,
  DashboardWidgetPlacement,
  isDashboardWidgetId,
} from './types';

export type DashboardSpanSlot = 'half' | 'full';

export interface DashboardLayoutRow {
  readonly span: DashboardSpanSlot;
  readonly widgets: readonly DashboardWidgetPlacement[];
}

const WARRANTS_WIDGET_IDS: readonly DashboardWidgetId[] = [
  'issuer_ranking_notional',
  'issuer_ranking_products',
  'currency_allocation',
  'products_near_expiry',
  'top_underlying_exposure',
  'events_calendar',
  'next_events',
];

export const getVisibleDashboardWidgetIds = (
  layout: DashboardLayout
): ReadonlySet<DashboardWidgetId> => {
  const ids = new Set<DashboardWidgetId>();
  for (const widget of layout.widgets) {
    if (isDashboardWidgetId(widget.id)) {
      ids.add(widget.id);
    }
  }
  return ids;
};

export const layoutNeedsWarrantsToggle = (layout: DashboardLayout): boolean => {
  const visible = getVisibleDashboardWidgetIds(layout);
  return WARRANTS_WIDGET_IDS.some((id) => visible.has(id));
};

/**
 * Groups ordered widgets into rows.
 * widthSpan 1 → half, widthSpan 2 → full.
 * Mobile (forceSingleColumn) and columnCount 1 stack every widget full width,
 * keeping user order. Stored widthSpan is unchanged so 2-column layout can restore it.
 */
export const buildDashboardLayoutRows = (
  layout: DashboardLayout,
  options: { readonly forceSingleColumn: boolean }
): DashboardLayoutRow[] => {
  const ordered = [...layout.widgets]
    .filter((widget) => isDashboardWidgetId(widget.id))
    .sort((left, right) => left.order - right.order);

  const useSingleColumn = options.forceSingleColumn || layout.columnCount === 1;

  if (useSingleColumn) {
    return ordered.map((widget) => ({
      span: 'full' as const,
      widgets: [widget],
    }));
  }

  const rows: DashboardLayoutRow[] = [];
  let pendingHalf: DashboardWidgetPlacement[] = [];

  const flushHalfRow = (): void => {
    if (pendingHalf.length === 0) {
      return;
    }
    rows.push({ span: 'half', widgets: pendingHalf });
    pendingHalf = [];
  };

  for (const widget of ordered) {
    const span: DashboardSpanSlot = widget.widthSpan >= 2 ? 'full' : 'half';
    if (span === 'full') {
      flushHalfRow();
      rows.push({ span: 'full', widgets: [widget] });
      continue;
    }

    pendingHalf.push(widget);
    if (pendingHalf.length === 2) {
      flushHalfRow();
    }
  }

  flushHalfRow();
  return rows;
};
